import type {
  CleanupApplyOrgMerge,
  CleanupApplyPairPromotion,
  CleanupApplyPlan,
  CleanupTypeVersion,
  DivergentGroup,
  GroupResolution,
  LooseEndDecision,
  LooseEndSet,
  RawTypeDefaults,
} from '../types';

import { personalKey } from './deriveWizardData';

export interface BuildApplyPlanInput {
  divergentGroups: DivergentGroup[];
  personalClusters: DivergentGroup[];
  /** Divergent groups keyed by nameKey; personal clusters by personalKey(nameKey). */
  resolutions: Readonly<Record<string, GroupResolution>>;
  quickWinSets: LooseEndSet[];
  looseEndSets: LooseEndSet[];
  /** Committed decisions, keyed by set.key. */
  quickWinDecisions: Readonly<Record<string, LooseEndDecision>>;
  looseEndDecisions: Readonly<Record<string, LooseEndDecision>>;
  restoreTypeIds: readonly string[];
  staleArchivedIds: readonly string[];
  catalogArchivedIds: readonly string[];
  setAsideArchivedIds: readonly string[];
  /** Set-aside types the admin kept-and-renamed → plain PATCH renames. */
  renames: ReadonlyArray<{ typeId: string; name: string }>;
}

const isArchive = (d: LooseEndDecision | undefined): boolean =>
  Boolean(d?.archive);

const isMerge = (d: LooseEndDecision | undefined): boolean =>
  Boolean(d?.merge && !d.archive);

/** A raw-default value counts as "set" unless it's null/undefined or empty. */
const isSet = (value: unknown): boolean =>
  value !== null &&
  value !== undefined &&
  !(Array.isArray(value) && value.length === 0);

/**
 * Field-wise union of raw defaults across a near-match set. Near matches are
 * conflict-free by construction (a field is set on at most one side, or all
 * agree), so first-set-wins per column is the merged result — and it includes
 * the keeper, so the endpoint can apply it as the keeper's full new defaults.
 */
const reconciledRawDefaults = (
  versions: CleanupTypeVersion[]
): RawTypeDefaults => {
  const pick = <K extends keyof RawTypeDefaults>(key: K): RawTypeDefaults[K] => {
    for (const v of versions) {
      if (isSet(v.rawDefaults[key])) return v.rawDefaults[key];
    }
    return versions[0]?.rawDefaults[key] ?? null;
  };
  return {
    default_duration: pick('default_duration'),
    default_modality: pick('default_modality'),
    default_place_of_service: pick('default_place_of_service'),
    default_cpt_codes: pick('default_cpt_codes'),
    default_icd10_codes: pick('default_icd10_codes'),
    telehealth: pick('telehealth'),
    default_action_rules: pick('default_action_rules'),
  };
};

const trimmedOrUndefined = (name: string | undefined): string | undefined =>
  name?.trim() ? name.trim() : undefined;

/**
 * Assemble the normalized apply plan from the wizard's decision state.
 *
 * Archives are the terminal reductions (deduped ids). Merges/promotions map to
 * the two engine verbs by the winner's template scope: a winner on a
 * user-scoped template promotes its template alongside the type
 * (pair_promotion, GDN-2292); anything else folds into an org-scoped keeper
 * (org_merge, GDN-2293). Near-match merges carry the field-wise reconciled
 * defaults. Freed templates are the merged-away losers' user templates.
 */
export const buildApplyPlan = (input: BuildApplyPlanInput): CleanupApplyPlan => {
  const archiveIds = new Set<string>([
    ...input.staleArchivedIds,
    ...input.catalogArchivedIds,
    ...input.setAsideArchivedIds,
  ]);
  const pairPromotions: CleanupApplyPairPromotion[] = [];
  const orgMerges: CleanupApplyOrgMerge[] = [];
  const freedTemplateIds = new Set<string>();

  // --- make-shared resolutions (Duplicates + Personal copies lanes) --------
  const resolveGroups = (
    groups: DivergentGroup[],
    keyOf: (nameKey: string) => string
  ): void => {
    for (const group of groups) {
      const resolution = input.resolutions[keyOf(group.nameKey)];
      if (resolution?.kind === 'archive_all') {
        for (const v of group.versions) archiveIds.add(v.id);
        continue;
      }
      if (resolution?.kind !== 'make_shared') continue;
      const winner = group.versions.find((v) => v.id === resolution.winnerId);
      if (!winner) continue;

      const setAside = new Set(resolution.setAsideIds ?? []);
      const loserIds = group.versions
        .filter((v) => v.id !== winner.id && !setAside.has(v.id))
        .map((v) => v.id);
      // A merged-away loser's user template is freed when it differs from the
      // survivor's (the server re-checks references before archiving it).
      for (const v of group.versions) {
        if (v.id === winner.id || setAside.has(v.id)) continue;
        if (
          v.templateScope === 'user' &&
          v.templateId &&
          v.templateId !== winner.templateId
        ) {
          freedTemplateIds.add(v.templateId);
        }
      }

      const sharedName = trimmedOrUndefined(resolution.sharedName);
      if (winner.templateScope === 'user') {
        pairPromotions.push({
          winner_id: winner.id,
          member_ids: group.versions.map((v) => v.id),
          ...(sharedName ? { shared_name: sharedName } : {}),
          ...(setAside.size ? { set_aside_ids: [...setAside] } : {}),
        });
      } else {
        orgMerges.push({
          keeper_id: winner.id,
          loser_ids: loserIds,
          ...(sharedName ? { keep_name: sharedName } : {}),
          ...(resolution.adoptDefaultsFromId
            ? { adopt_defaults_from_id: resolution.adoptDefaultsFromId }
            : {}),
        });
      }
    }
  };
  resolveGroups(input.divergentGroups, (k) => k);
  resolveGroups(input.personalClusters, personalKey);

  // --- set merges / archives (Quick wins + Loose ends) ---------------------
  const resolveSets = (
    sets: LooseEndSet[],
    decisions: Readonly<Record<string, LooseEndDecision>>
  ): void => {
    for (const set of sets) {
      const decision = decisions[set.key];
      if (isArchive(decision)) {
        for (const e of set.entries) archiveIds.add(e.version.id);
        continue;
      }
      if (!isMerge(decision) || set.entries.length < 2) continue;
      const keeper =
        set.entries.find((e) => e.name === decision?.keepName)?.version ??
        set.entries[0].version;
      const loserIds = set.entries
        .filter((e) => e.version.id !== keeper.id)
        .map((e) => e.version.id);
      const keepName = trimmedOrUndefined(decision?.keepName);

      // A merged-away member's user template is freed when it differs from the
      // keeper's — mirrors resolveGroups so Quick Wins / Loose Ends merges don't
      // strand orphaned personal templates. Runs for both branches below (the
      // server re-checks references before archiving).
      for (const e of set.entries) {
        const member = e.version;
        if (member.id === keeper.id) continue;
        if (
          member.templateScope === 'user' &&
          member.templateId &&
          member.templateId !== keeper.templateId
        ) {
          freedTemplateIds.add(member.templateId);
        }
      }

      // A whole set on a user template promotes that template with the type.
      if (keeper.templateScope === 'user') {
        pairPromotions.push({
          winner_id: keeper.id,
          member_ids: set.entries.map((e) => e.version.id),
          ...(keepName ? { shared_name: keepName } : {}),
        });
      } else {
        orgMerges.push({
          keeper_id: keeper.id,
          loser_ids: loserIds,
          ...(keepName ? { keep_name: keepName } : {}),
          ...(set.near
            ? {
                reconciled_defaults: reconciledRawDefaults(
                  set.entries.map((e) => e.version)
                ),
              }
            : {}),
        });
      }
    }
  };
  resolveSets(input.quickWinSets, input.quickWinDecisions);
  resolveSets(input.looseEndSets, input.looseEndDecisions);

  return {
    archiveTypeIds: [...archiveIds],
    restoreTypeIds: [...new Set(input.restoreTypeIds)],
    archiveTemplateIds: [...freedTemplateIds],
    pairPromotions,
    orgMerges,
    renames: input.renames.map((r) => ({ typeId: r.typeId, name: r.name })),
  };
};
